import { useState, useEffect } from "react";
import { SuccessJourney, ScaleAndSpeed, DealersFirst, SupportAndTech } from "@/components/shared/DealerAdvantage";
import { SEOHead } from "@/components/shared/SEOHead";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { CheckCircle, ArrowRight, ClipboardList, Users, Store, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getPageContent } from "@/lib/api";

const ICON_MAP = { ClipboardList, Users, Store, Rocket, CheckCircle };

function getSection(sections, name, fallback = {}) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return fallback;
  return { ...fallback, ...found, metadata: { ...(fallback.metadata || {}), ...(found.metadata || {}) } };
}

const defaultSteps = [
  { icon: "ClipboardList", title: "Apply to Become a Dealer", description: "Fill out our simple online application form. We'll review your information and reach out within 24-48 hours to discuss the next steps." },
  { icon: "Users", title: "Onboarding & Training", description: "Once approved, our dedicated team walks you through everything: systems setup, product training, marketing materials, and compliance requirements." },
  { icon: "Store", title: "Set Up Your Store", description: "We help you get your retail location ready with signage, inventory, POS setup, and all the tools you need to start selling on day one." },
  { icon: "Rocket", title: "Launch & Grow", description: "Start activating customers and earning commissions. Our ongoing support, marketing assistance, and performance insights help you scale your business." },
];

const defaultBenefits = [
  { title: "Fast Approval", description: "Most applications are reviewed and approved within 48 hours." },
  { title: "Full Support", description: "Dedicated account managers and 24/7 support from day one." },
  { title: "Proven Results", description: "Hundreds of successful dealers growing their business with us." },
];

export default function OurProcessPage() {
  const [sections, setSections] = useState([]);
  useEffect(() => { getPageContent("our-process").then(setSections).catch(() => {}); }, []);

  const hero = getSection(sections, "hero", { title: "Our Process", content: "From application to activation, we make it simple to become a successful wireless dealer. Here's how we get you up and running.", metadata: { subtitle: "HOW IT WORKS" } });
  const steps = getSection(sections, "steps", { metadata: { items: defaultSteps } });
  const benefits = getSection(sections, "benefits", { title: "Why Dealers Choose Us", metadata: { items: defaultBenefits } });
  const cta = getSection(sections, "cta", { title: "Ready to Get Started?", content: "Join Prime Media Wireless today and start building your wireless retail business.", button_text: "Apply Now", button_link: "/become-dealer" });

  const stepItems = steps.metadata?.items || defaultSteps;
  const benefitItems = benefits.metadata?.items || defaultBenefits;

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="our-process"
        fallback={{
          meta_title: "How to Become a Total Wireless Dealer | Our Process | Prime Media Wireless",
          meta_description: "See how easy it is to become an authorized Total Wireless and Verizon Value dealer with Prime Media Wireless. Simple 4-step process.",
          canonical_url: `${window.location.origin}/our-process`,
        }}
      />
      {/* Hero */}
      <section className="bg-navy py-20" data-testid="process-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">{hero.metadata?.subtitle}</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">{hero.title}</h1>
            <p className="text-slate-300 text-lg font-body">{hero.content}</p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-white" data-testid="process-steps">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {stepItems.map((step, i) => {
              const Icon = ICON_MAP[step.icon] || ClipboardList;
              const num = String(i + 1).padStart(2, "0");
              const isEven = i % 2 === 1;
              return (
                <div key={i} className={`flex flex-col md:flex-row items-center gap-8 ${isEven ? "md:flex-row-reverse" : ""}`} data-testid={`process-step-${num}`}>
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 rounded-2xl bg-teal/10 flex items-center justify-center relative">
                      <Icon className="w-10 h-10 text-teal" />
                      <span className="absolute -top-3 -left-3 w-8 h-8 bg-navy text-white text-sm font-heading font-bold rounded-full flex items-center justify-center">
                        {num}
                      </span>
                    </div>
                  </div>
                  <div className={`flex-1 ${isEven ? "md:text-right" : ""}`}>
                    <h3 className="font-heading font-bold text-xl text-navy mb-2">{step.title}</h3>
                    <p className="text-slate-600 font-body leading-relaxed">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-slate-50" data-testid="process-benefits">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl text-navy mb-10">{benefits.title}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefitItems.map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <CheckCircle className="w-8 h-8 text-teal mb-3 mx-auto" />
                <h3 className="font-heading font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm font-body">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SuccessJourney />
      <ScaleAndSpeed />
      <DealersFirst />
      <SupportAndTech />

      {/* CTA */}
      <section className="py-16 bg-navy" data-testid="process-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-4">{cta.title}</h2>
          <p className="text-slate-300 font-body mb-8">{cta.content}</p>
          <Link to={cta.button_link || "/become-dealer"}>
            <Button className="bg-teal hover:bg-teal/90 text-white px-8 py-3 text-base" data-testid="process-cta-btn">
              {cta.button_text || "Apply Now"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
